import React from 'react';
import { Path, CheckCircle, CaretRight, Trash } from '@phosphor-icons/react';
import { getDistance } from '../utils/maps';
import { formatDuration, formatApproxDistance } from '../utils/measures';
import { plural } from '../utils/plural';
import telegramService from '../services/telegram';

const STOPS = ['точка', 'точки', 'точек'];

const USER_ROUTE_PROGRESS_KEY = 'user_route_progress_';

/**
 * Прогресс прохождения личного маршрута.
 *
 * Пока живёт только в `localStorage` — перенос в Strapi запланирован
 * Этапом 7. До него цифры верны лишь на том устройстве, где шли.
 */
export const getRouteProgress = (routeId, totalStops) => {
  try {
    const saved = localStorage.getItem(USER_ROUTE_PROGRESS_KEY + routeId);
    if (!saved) return null;
    const { completed = [], skipped = [] } = JSON.parse(saved);
    const doneCount = completed.length;
    if (doneCount === 0) return null;
    const allDone = doneCount + skipped.length >= totalStops || doneCount >= totalStops;
    return { doneCount, allDone };
  } catch {
    return null;
  }
};

/** Длина маршрута по точкам — сумма отрезков по прямой */
const calcRouteDistance = (stops) => {
  let total = 0;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    if (a.latitude && a.longitude && b.latitude && b.longitude) {
      total += getDistance(a.latitude, a.longitude, b.latitude, b.longitude);
    }
  }
  return total;
};

/**
 * Плашка личного маршрута — строка списка, а не постер.
 *
 * Форма намеренно не такая, как у карточки места: место — вертикальная
 * карточка с фото 3:4, маршрут — горизонтальная строка с миниатюрой
 * 72px слева. Разные сущности читаются формой, до чтения текста.
 *
 * Фото было 160px во всю ширину, под ним метрики и кнопка во всю
 * ширину: пять маршрутов не влезали в пять экранов прокрутки. При этом
 * обложка маршрута — это снимок его первой точки, то есть узнают
 * маршрут всё равно по названию. Миниатюры хватает.
 *
 * Кнопки внутри больше нет. Она несла «Продолжить — 1/5», но то же
 * состояние показывают полоса прогресса и подпись «1/5 пройдено»
 * в метриках — а вся плашка и так кликабельна и ведёт туда же.
 *
 * Справа корзина и шеврон. Корзина — чтобы удалять из списка, а не
 * заходя внутрь ради одной кнопки; шеврон остаётся, потому что главное
 * действие плашки всё-таки «открыть». Без него корзина у правого края
 * читалась как действие всей строки: плашка выглядела кнопкой удаления.
 */
const UserRouteCard = ({ userRoute, onOpen, onDelete }) => {
  const stops = userRoute.stops || [];
  const stopsCount = stops.length;
  const progress = getRouteProgress(userRoute.id, stopsCount);
  const coverImage = stops[0]?.imageUrl || null;

  const distanceKm = stopsCount >= 2 ? calcRouteDistance(stops) : 0;
  // Пеший темп ≈4 км/ч, отсюда 15 минут на километр
  const walkMinutes = Math.round(distanceKm * 15);

  const dateStr = userRoute.scheduledDate
    ? new Date(userRoute.scheduledDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
    : null;

  const done = Boolean(progress?.allDone);
  const started = Boolean(progress) && !done;

  const progressPercent = progress && stopsCount > 0
    ? Math.round((progress.doneCount / stopsCount) * 100)
    : 0;

  // Состояние прохождения идёт первым: оно важнее длины и времени,
  // когда маршрут уже начат
  const metrics = [
    started ? `${progress.doneCount}/${stopsCount} пройдено` : null,
    !started && stopsCount > 0 ? `${stopsCount} ${plural(stopsCount, STOPS)}` : null,
    formatDuration(walkMinutes),
    formatApproxDistance(distanceKm),
  ].filter(Boolean);

  const open = () => { telegramService.hapticFeedback('light'); onOpen(); };

  return (
    <div
      onClick={open}
      className="mb-2 cursor-pointer overflow-hidden rounded-lg border border-theme-border bg-theme-card transition-transform duration-fast ease-out active:scale-98"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div className="flex items-center gap-3 p-3">
        <div className="relative h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-md">
          {coverImage ? (
            <img src={coverImage} alt={userRoute.title} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-theme-card-secondary">
              <Path size={24} weight="light" className="text-theme-text-hint" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {/* Метка «Пройден» — единственное место зелёного здесь: он отмечает
              достигнутое, а не призывает к действию. Брендбук 6.2 */}
          {done && (
            <span
              className="mb-1 inline-flex items-center gap-1 text-kicker"
              style={{ color: 'var(--color-success)' }}
            >
              <CheckCircle size={13} weight="fill" />
              Пройден
            </span>
          )}
          {dateStr && !done && (
            <p className="tabular mb-0.5 text-kicker text-theme-text-hint">{dateStr}</p>
          )}

          <h3 className="truncate text-body font-semibold text-theme-text-primary">
            {userRoute.title}
          </h3>

          {metrics.length > 0 && (
            <p className="tabular mt-1 truncate text-caption text-theme-text-secondary">
              {metrics.join(' · ')}
            </p>
          )}
        </div>

        {/* Корзина в кружке, шеврон за ней: главное действие плашки —
            открыть, и стрелка это сообщает. Без обводки корзина у края
            читалась как действие всей строки, а не как кнопка в ней.
            stopPropagation, чтобы нажатие не открывало прогулку */}
        {onDelete && (
          <button
            onClick={e => { e.stopPropagation(); telegramService.hapticFeedback('light'); onDelete(); }}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center border-0 bg-transparent transition-transform duration-fast ease-out active:scale-90"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label={`Удалить маршрут «${userRoute.title}»`}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-theme-border bg-theme-card-secondary text-theme-text-hint">
              <Trash size={15} />
            </span>
          </button>
        )}

        <CaretRight size={18} className="-ml-1 flex-shrink-0 text-theme-text-hint" />
      </div>

      {/* Полоса прогресса — только у начатого: у пройденного она была бы
          сплошной и ничего не сообщала. Синяя, а не зелёная: прохождение
          в процессе — это состояние, а зелёный в проекте отмечает
          достигнутое (метка «Пройден» ниже) */}
      {started && (
        <div className="h-[3px]" style={{ background: 'var(--color-card-secondary)' }}>
          <div
            className="h-full transition-[width] duration-base ease-out"
            style={{ width: `${progressPercent}%`, background: 'var(--color-action)' }}
          />
        </div>
      )}
    </div>
  );
};

export default UserRouteCard;
